import { supabase } from '../lib/supabase'

export type UserType = 'ibedrijf' | 'inspecteur' | 'opdrachtgever' | 'installateur' | 'unknown'

export interface UserTypeResult {
  types: UserType[]  // User can have multiple types
  isAllowed: boolean
  redirectUrl: string | null
}

/**
 * Check what type of user is logged in by querying tables
 * A user can have multiple types (e.g., both ibedrijf and opdrachtgever)
 * Portal package is for: opdrachtgevers and installateurs
 * Frontend package is for: ibedrijven and inspecteurs
 */
export async function checkUserType(userId: string): Promise<UserTypeResult> {
  // Run all checks in parallel for efficiency
  const [userResult, ibedrijfResult, inspecteurResult, opdrachtgeverResult, installateurResult] = await Promise.all([
    supabase.from('users').select('role').eq('id', userId).maybeSingle(),
    supabase.from('ibedrijven').select('id').eq('id', userId).maybeSingle(),
    supabase.from('inspecteurs').select('id').eq('id', userId).maybeSingle(),
    supabase.from('opdrachtgevers').select('id').eq('id', userId).maybeSingle(),
    supabase.from('installateurs').select('id').eq('id', userId).maybeSingle(),
  ])

  const queryErrors = [
    userResult.error,
    ibedrijfResult.error,
    inspecteurResult.error,
    opdrachtgeverResult.error,
    installateurResult.error,
  ].filter((error): error is NonNullable<typeof userResult.error> => error !== null)

  if (queryErrors.length > 0) {
    throw new Error(`Failed to resolve user type: ${queryErrors.map(error => error.message).join('; ')}`)
  }

  const role = userResult.data?.role
  const typeSet = new Set<UserType>()

  if (role === 'ibedrijf' || role === 'inspecteur' || role === 'opdrachtgever' || role === 'installateur') {
    typeSet.add(role)
  }

  if (ibedrijfResult.data) typeSet.add('ibedrijf')
  if (inspecteurResult.data) typeSet.add('inspecteur')
  if (opdrachtgeverResult.data) typeSet.add('opdrachtgever')
  if (installateurResult.data) typeSet.add('installateur')

  const types = [...typeSet]

  // Portal allows opdrachtgevers and installateurs.
  const allowedTypes: UserType[] = ['opdrachtgever', 'installateur']
  const isAllowed = types.some(type => allowedTypes.includes(type))

  if (types.length === 0) {
    return {
      types: ['unknown'],
      isAllowed: false,
      redirectUrl: null
    }
  }

  return {
    types,
    isAllowed,
    redirectUrl: isAllowed ? null : getFrontendUrl()
  }
}

/**
 * Get the portal URL based on environment
 */
export function getPortalUrl(): string {
  // In development, portal runs on a different port
  if (import.meta.env.DEV) {
    return 'https://portal.inspeqt-dev.net'
  }
  // In production, use the portal subdomain
  return import.meta.env.VITE_PORTAL_URL || 'https://portal.inspeqt.net'
}

/**
 * Get the frontend URL based on environment
 */
export function getFrontendUrl(): string {
  // In development
  if (import.meta.env.DEV) {
    return 'https://frontend.inspeqt-dev.net'
  }
  // In production
  return import.meta.env.VITE_FRONTEND_URL || 'https://app.inspeqt.net'
}

export function useUserTypeCheck() {
  return {
    checkUserType,
    getPortalUrl,
    getFrontendUrl
  }
}
